import { useEffect, useState } from "react";
import { SearchPageShell, AnswerBlock } from "@/pages/search/SearchDocLayout";
import { fetchCapabilities } from "@/lib/searchContent";
import {
  CapLink,
  KnowledgeExplore,
  KnowledgeHeroNav,
} from "@/pages/search/KnowledgeChrome";

function yearOf(row) {
  const raw = row.issued_date || row.start_date || "";
  return String(raw).slice(0, 4);
}

function isExternalHref(href) {
  return /^https?:\/\//i.test(href || "");
}

const CAPABILITY_HREF = {
  website: { href: "/capabilities#layer-website", label: "WEBSITE" },
  webdev: { href: "/capabilities#layer-webdev", label: "WEB DEVELOPMENT" },
  saas: { href: "/services/saas", label: "SaaS" },
  admin: { href: "/services/admin-cms", label: "ADMIN / CMS" },
  ai: { href: "/services/ai", label: "AI" },
  automation: { href: "/services/automation", label: "AUTOMATION" },
  search: { href: "/services/search", label: "SEARCH" },
};

const GUIDE_HREF = {
  "guide-homepage-agency": "/guides/homepage-agency-criteria",
  "guide-why-dsitesolution": "/guides/why-dsitesolution",
  "guide-agency-vs-developer": "/guides/design-agency-vs-developer",
  "guide-saas-vendor": "/guides/saas-vendor-criteria",
  "guide-admin-cms": "/guides/why-admin-cms",
  "guide-ai-saas": "/guides/ai-saas-integration",
  "guide-workflow-automation": "/guides/workflow-automation-steps",
  "guide-frontend-backend": "/guides/frontend-backend-custom-web",
  "guide-custom-web": "/guides/custom-web-vs-homepage",
};

function MetricCard({ row }) {
  const label = row.public_label || "누적 프로젝트";
  const value = row.metric_value || "832";
  const unit = row.metric_unit || "건";
  const text = row.public_title || `${label} ${value}${unit}`;
  const capLinks = (row.related_capabilities || [])
    .map((id) => CAPABILITY_HREF[id])
    .filter(Boolean);
  const serviceLinks = (row.related_services || []).slice(0, 4).map((id) => ({
    href: `/services/${String(id).replace(/^svc-/, "")}`,
    label: String(id).replace(/^svc-/, "").replace(/-/g, " "),
  }));
  const guideLinks = (row.related_guides || [])
    .map((id) => (GUIDE_HREF[id] ? { href: GUIDE_HREF[id], label: "가이드" } : null))
    .filter(Boolean)
    .slice(0, 2);
  const portfolioLinks = (row.related_portfolios || []).slice(0, 2).map((slug) => ({
    href: `/portfolio/${slug}`,
    label: "포트폴리오",
  }));
  const links = [...capLinks, ...serviceLinks.slice(0, 3), ...guideLinks, ...portfolioLinks];
  return (
    <article className="ds-auth-metric" data-testid="authority-metric">
      <p className="ds-auth-metric__value">{value}</p>
      <p className="ds-auth-metric__label">{label}</p>
      <p>{text}</p>
      {links.length ? (
        <p className="ds-search-meta">
          {links.map((item) => (
            <CapLink key={`${item.href}-${item.label}`} to={item.href}>{item.label}</CapLink>
          ))}
        </p>
      ) : null}
    </article>
  );
}

function AuthorityCard({ row }) {
  const summary = row.public_description || row.public_summary;
  const verifyHref = row.external_verification_url || "";
  const coverage = row.coverage || [];
  const year = yearOf(row);
  const hasDetail = Boolean(row.issuer || year || coverage.length || verifyHref);
  return (
    <article className="ds-cap-layer ds-auth-card">
      <h3>{row.public_title}</h3>
      <p>{summary}</p>
      <p className="ds-search-meta">
        {row.verification_label ? <span>{row.verification_label}</span> : null}
      </p>
      {hasDetail ? (
        <details className="ds-auth-detail">
          <summary>자세히</summary>
          {row.issuer ? <p>Provider {row.issuer}</p> : null}
          {year ? <p>Completed {year}</p> : null}
          {coverage.length ? <p>Coverage {coverage.join(" · ")}</p> : null}
          {verifyHref ? (
            isExternalHref(verifyHref) ? (
              <p><a href={verifyHref} target="_blank" rel="noreferrer">자격 확인</a></p>
            ) : (
              <p><CapLink to={verifyHref}>근거</CapLink></p>
            )
          ) : null}
        </details>
      ) : null}
    </article>
  );
}

export default function CapabilitiesPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchCapabilities()
      .then(setData)
      .catch(() => setData(null));
  }, []);

  const positioning = data?.positioning || {};
  const stack = data?.stack || [];
  const comparison = data?.comparison || [];
  const qa = data?.qa || [];

  return (
    <SearchPageShell testId="capabilities-page" wide>
      <KnowledgeHeroNav crumbs={[{ href: "/", label: "홈" }, { label: "Capabilities" }]} />
      <p className="ds-search-kicker">CAPABILITIES</p>
      <h1 className="ds-search-h1">{positioning.one_liner || "WEB + SYSTEM + SAAS + AI + AUTOMATION"}</h1>
      <p className="ds-search-summary">
        {positioning.entity ||
          "DSITESOLUTION은 일반적인 홈페이지 제작사가 아니라, 웹사이트·맞춤형 웹개발·관리자/CMS/CRM·SaaS 플랫폼·AI·업무 자동화·SEO/AEO/GEO를 한 흐름으로 연결하는 디지털 기술 기업입니다."}
      </p>
      <AnswerBlock answer={positioning.headline} />

      <section className="ds-cap-section">
        <h2>구축 스택</h2>
        <ol className="ds-cap-stack" data-testid="capability-stack">
          {stack.map((row, index) => (
            <li key={row.id || row.label}>
              <CapLink to={row.href || "/services"}>{row.label}</CapLink>
              {row.evidence ? (
                <CapLink className="ds-cap-evidence" to={row.evidence}>
                  근거
                </CapLink>
              ) : null}
              {index < stack.length - 1 ? <span className="ds-cap-arrow" aria-hidden="true">→</span> : null}
            </li>
          ))}
        </ol>
      </section>

      {data?.layers?.length ? (
      <section className="ds-cap-section" id="layers">
        <h2>전문 영역</h2>
        <p className="ds-search-summary">홈페이지 제작 자산을 유지한 채, 그 위에 개발·SaaS·관리자·AI·자동화를 같은 회사 범위로 둡니다.</p>
        <div className="ds-cap-layers">
          {(data?.layers || []).map((row) => (
            <article key={row.id} id={`layer-${row.id}`} className="ds-cap-layer">
              <h3>{row.label}</h3>
              <p>{row.summary}</p>
              {row.topics?.length ? (
                <ul>
                  {row.topics.map((topic) => (
                    <li key={topic}>{topic}</li>
                  ))}
                </ul>
              ) : null}
              <p className="ds-search-meta">
                {row.href ? <CapLink to={row.href}>관련 서비스</CapLink> : null}
                {(row.guide_hrefs || []).map((href) => (
                  <CapLink key={href} to={href}>가이드</CapLink>
                ))}
                {row.evidence ? <CapLink to={row.evidence}>근거</CapLink> : null}
              </p>
            </article>
          ))}
        </div>
      </section>
      ) : null}

      {data?.industries?.length ? (
      <section className="ds-cap-section" id="industries">
        <h2>산업 적용</h2>
        <p className="ds-search-summary">산업 페이지는 회사 정체성 전체가 아닙니다. 병원 홈페이지 검색 자산은 유지합니다.</p>
        {(data?.industries || []).map((row) => (
          <article key={row.id} id={`industry-${row.id}`} className="ds-cap-layer">
            <h3>{row.label}</h3>
            <p>{row.summary}</p>
            <p className="ds-search-meta">
              {row.href ? <CapLink to={row.href}>관련 서비스</CapLink> : null}
              {(row.guide_hrefs || []).map((href) => (
                <CapLink key={href} to={href}>가이드</CapLink>
              ))}
            </p>
          </article>
        ))}
      </section>
      ) : null}

      {data?.authority?.facts?.length ? (
      <section className="ds-cap-section" id="authority" data-testid="public-authority">
        <p className="ds-search-kicker">WHY DSITESOLUTION</p>
        <h2>전문성 · 경력</h2>
        <p className="ds-search-summary">{data.authority.disclaimer}</p>
        {(data.authority.groups?.METRIC || []).length ? (
          <div className="ds-auth-metrics">
            {(data.authority.groups.METRIC).map((row) => (
              <MetricCard key={row.id} row={row} />
            ))}
          </div>
        ) : null}
        {data.authority.people?.length ? (
          <div className="ds-auth-people">
            {data.authority.people.map((person) => (
              <article key={person.id} className="ds-cap-layer">
                <p className="ds-search-kicker">PERSON</p>
                <h3>{person.name} · {person.role}</h3>
                <p>{person.summary}</p>
                {person.expertise?.length ? (
                  <ul>
                    {person.expertise.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
        ) : null}
        {[
          "COMPANY_AUTHORITY",
          "TECHNICAL_EXPERTISE",
          "PROFESSIONAL_BACKGROUND",
          "CREDENTIALS",
          "EXPERIENCE",
          "INTELLECTUAL_PROPERTY",
          "EXPERTISE",
          "GLOBAL_EXPERIENCE",
        ].map((group) => {
          const rows = data.authority.groups?.[group] || [];
          if (!rows.length) return null;
          const labels = {
            COMPANY_AUTHORITY: "COMPANY",
            TECHNICAL_EXPERTISE: "TECHNICAL EXPERTISE",
            PROFESSIONAL_BACKGROUND: "PROFESSIONAL BACKGROUND",
            CREDENTIALS: "CREDENTIALS",
            EXPERIENCE: "PROJECT EXPERIENCE",
            INTELLECTUAL_PROPERTY: "INTELLECTUAL PROPERTY",
            EXPERTISE: "EXPERTISE",
            GLOBAL_EXPERIENCE: "GLOBAL EXPERIENCE",
          };
          return (
            <div key={group} className="ds-auth-group">
              <h3 className="ds-auth-group__title">{labels[group]}</h3>
              <div className="ds-cap-layers">
                {rows.map((row) => (
                  <AuthorityCard key={row.id} row={row} />
                ))}
              </div>
            </div>
          );
        })}
      </section>
      ) : null}

      <section className="ds-cap-section">
        <h2>일반 홈페이지 제작사와 다른 점</h2>
        <div className="ds-cap-table-wrap">
          <table className="ds-cap-table" data-testid="capability-comparison">
            <thead>
              <tr>
                <th>항목</th>
                <th>일반적인 홈페이지 제작사</th>
                <th>DSITESOLUTION</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((row) => (
                <tr key={row.item}>
                  <td>{row.item}</td>
                  <td>{row.typical}</td>
                  <td>
                    {row.href ? <CapLink to={row.href}>{row.dsite}</CapLink> : row.dsite}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="ds-cap-section" data-testid="capability-qa">
        <h2>AI와 사용자가 바로 확인할 수 있는 답</h2>
        {qa.map((row) => (
          <article key={row.id || row.question} className="ds-cap-qa">
            <h3>{row.question}</h3>
            <p>{row.answer}</p>
            {row.evidence_href ? (
              <p className="ds-search-meta">
                근거: <CapLink to={row.evidence_href}>{row.evidence_label || row.evidence_href}</CapLink>
              </p>
            ) : null}
          </article>
        ))}
      </section>

      <p className="ds-cap-links">
        <CapLink to="/guides/why-dsitesolution">왜 DSITESOLUTION인가</CapLink>
        <CapLink to="/guides">가이드</CapLink>
        <CapLink to="/portfolio/brand-web">포트폴리오</CapLink>
        <CapLink to="/">홈</CapLink>
      </p>
      <KnowledgeExplore
        heading="이어서 보기"
        fromCapabilities
        links={[
          { href: "/capabilities", label: "Capabilities 전체보기", active: true },
          { href: "/guides", label: "관련 Guide" },
          { href: "/portfolio/brand-web", label: "관련 Portfolio" },
          { href: "/", label: "홈으로" },
        ]}
        contact
      />
    </SearchPageShell>
  );
}
